import { createContext, useContext, ReactNode } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Lets the RFQ feature resolve its rfqId from an explicit provider instead of
 * the route param. The standalone Auto RFQ page (`/auto-rfq/:id`) never mounts
 * the provider, so `useRfqId()` falls back to `useParams().id` and behaves
 * exactly as before. Hosts that embed the RFQ machinery without that route
 * param (e.g. Quotes V2 details, where the id comes from `quote.rfqId`) wrap
 * their subtree in `RfqIdProvider` to inject it.
 */
const RfqIdContext = createContext<string | undefined>(undefined);

interface RfqIdProviderProps {
  rfqId?: string;
  children: ReactNode;
}

export const RfqIdProvider = ({ rfqId, children }: RfqIdProviderProps) => (
  <RfqIdContext.Provider value={rfqId}>{children}</RfqIdContext.Provider>
);

export const useRfqId = (): string | undefined => {
  const injectedRfqId = useContext(RfqIdContext);
  const { id } = useParams<{ id: string }>();

  return injectedRfqId ?? id;
};
